import React, { useState, FC, ReactElement } from 'react'
import { Typography, Button, Result, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { s } from './style'
import { getStatuses } from '../../../../Graphql-Utilities'
import { HeavyOutlineButton } from '../../../../Components/UI'
import {
  WithdrawBriefEventFragment,
  useWithdrawBriefMutationMutation,
} from './WithdrawBrief.generated-types'
import { EventReleaseReason } from '../../../../types'

const { Text } = Typography

interface Props {
  event: WithdrawBriefEventFragment
  onCancelSuccess: () => void
}

export const WithdrawBrief: FC<Props> = ({ event, onCancelSuccess }): ReactElement => {
  const { t } = useTranslation()
  const [isConfirmWithdrawModalVisible, setIsConfirmWithdrawModalVisible] = useState(false)
  const [isFailedWithdrawModalVisible, setIsFailedWithdrawModalVisible] = useState(false)

  const [{ fetching }, cancelEvent] = useWithdrawBriefMutationMutation()

  const { isAwaitingResponses, isActiveWithResponses } = getStatuses(event)

  const handleWithdrawBrief = async (): Promise<void> => {
    setIsConfirmWithdrawModalVisible(false)

    const result = await cancelEvent({
      UUID: event?.UUID,
      releaseReason: EventReleaseReason.CancelledByBooker
    })

    if (result.error) {
      setIsFailedWithdrawModalVisible(true)
      return
    }

    onCancelSuccess()
  }

  const handleBackToMyEvents = (): void => {
    setIsConfirmWithdrawModalVisible(false)
    setIsFailedWithdrawModalVisible(false)
  }

  const handleViewConfirmWithdrawModal = (): void => setIsConfirmWithdrawModalVisible(true)

  const WithdrawCTA = (): ReactElement => (
    <HeavyOutlineButton
      loading={fetching}
      style={s.button}
      onClick={handleViewConfirmWithdrawModal}
      disabled={!(isAwaitingResponses || isActiveWithResponses)}>
      {t('eventHub.withdraw.primaryCta', 'Cancel event')}
    </HeavyOutlineButton>
  )

  const BackToEvents = (): ReactElement => (
    <HeavyOutlineButton onClick={handleBackToMyEvents}>
      {t('eventHub.withdraw.modal.back', 'Go back')}
    </HeavyOutlineButton>
  )

  const ConfirmWithdrawModal = (): ReactElement => (
    <Modal
      centered
      width={560}
      bodyStyle={s.modal}
      onCancel={handleBackToMyEvents}
      visible={isConfirmWithdrawModalVisible}
      footer={[
        <section style={s.footer} key={'1'}>
          <BackToEvents />
          <Button type='primary' key='cancel' onClick={async () => await handleWithdrawBrief()}>
            {t('eventHub.withdraw.modal.confirm', 'Cancel event')}
          </Button>
        </section>,
      ]}>
      <Result
        title={
          <section style={s.result}>
            <Text style={s.title}>
              {t('eventHub.withdraw.modal.title', 'No longer need a space?')}
            </Text>
            <Text style={s.text}>
              {t(
                'eventHub.withdraw.modal.text',
                'Please cancel this enquiry if you no longer want to book.'
              )}
            </Text>
          </section>
        }
      />
    </Modal>
  )

  const FailedWithdrawModal = (): ReactElement => (
    <Modal
      bodyStyle={s.modal}
      centered
      onCancel={handleBackToMyEvents}
      visible={isFailedWithdrawModalVisible}
      footer={[
        <section style={s.confirmFooter} key='1'>
          <BackToEvents />
        </section>,
      ]}>
      <Result
        title={
          <section style={s.result}>
            <Text>
              {t('eventHub.withdraw.modal.error', 'An error prevented withdrawing your enquiry.')}
            </Text>
          </section>
        }
      />
    </Modal>
  )

  return (
    <>
      <WithdrawCTA />
      <ConfirmWithdrawModal />
      <FailedWithdrawModal />
    </>
  )
}
